// path: frontend/src/context/AuthProvider.tsx

import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import { auth } from "@/lib/firebase";
import {
  onIdTokenChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getIdToken,
  getIdTokenResult,
  type User,
} from "firebase/auth";
import { planFromClaims, type Plan } from "@/lib/plan-limits";

export type Claims = Record<string, unknown>;

export interface AuthContextType {
  user: User | null;
  claims: Claims | null;
  isAdmin: boolean;
  plan: Plan;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signupWithEmail: (email: string, password: string) => Promise<void>;
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isPopupLikelyBlocked(err: unknown): boolean {
  const code = (err as any)?.code ?? "";
  const msg = String((err as any)?.message ?? "");
  return (
    code === "auth/popup-blocked" ||
    code === "auth/popup-closed-by-user" ||
    msg.toLowerCase().includes("popup") ||
    msg.toLowerCase().includes("blocked")
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<Claims | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = useMemo(
    () =>
      Boolean(
        (claims as any)?.admin ||
        (claims as any)?.isAdmin ||
        (claims as any)?.role === "admin",
      ),
    [claims],
  );

  const plan = useMemo<Plan>(() => planFromClaims(claims), [claims]);

  const hydrate = async (u: User | null, force = false) => {
    if (!u) {
      setUser(null);
      setClaims(null);
      return;
    }

    try {
      const res = await getIdTokenResult(u, force);
      setUser(u);
      setClaims(res.claims || null);
    } catch (err) {
      console.error("Failed to hydrate auth claims:", err);
      setUser(u);
      setClaims(null);
    }
  };

  useEffect(() => {
    let active = true;

    const unsubscribe = onIdTokenChanged(auth, async (nextUser) => {
      try {
        await hydrate(nextUser, false);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    });

    void getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          await hydrate(result.user, true);
        }
      })
      .catch((err) => {
        console.error("Redirect auth result error:", err);
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    // Prefer popup; fallback to redirect when popup fails (Safari/Iframe issues)
    try {
      await signInWithPopup(auth, provider);
      await hydrate(auth.currentUser, true);
    } catch (err) {
      console.error("Google popup login failed:", err);

      // Fallback to redirect (more reliable on Safari / strict storage environments)
      if (isPopupLikelyBlocked(err)) {
        await signInWithRedirect(auth, provider);
        return; // redirect takes over navigation
      }

      // If it failed for a different reason, rethrow so UI can show a message
      throw err;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setClaims(null);
  };

  const loginWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    await hydrate(auth.currentUser, true);
  };

  const signupWithEmail = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
    await hydrate(auth.currentUser, true);
  };

  const getTokenFn = async (forceRefresh = false): Promise<string | null> => {
    const u = auth.currentUser;
    if (!u) return null;
    try {
      return await getIdToken(u, forceRefresh);
    } catch (err) {
      console.error("Failed to get ID token:", err);
      return null;
    }
  };

  const refreshUser = async () => {
    await hydrate(auth.currentUser, true);
  };

  const value: AuthContextType = {
    user,
    claims,
    isAdmin,
    plan,
    loading,
    login,
    logout,
    loginWithEmail,
    signupWithEmail,
    getToken: getTokenFn,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Convenience hook for consumers (including CreditProvider)
export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

export default AuthProvider;
